import { useBookingStore } from '~~/domains/booking';
import type { Currency } from '~~/domains/graphql';
import { useCurrenciesMap } from '~~/domains/referential';
import { convertTo } from '~~/helpers';

type CurrencyParameter = string | Currency['code'] | null | undefined;

function extractRef<T>(parameter: Ref<T> | T): T {
  return isRef(parameter) ? parameter.value : parameter;
}

export function useConvertToUserCurrency(
  amount: Ref<number> | number,
  currency: Ref<CurrencyParameter> | CurrencyParameter,
  targetCurrency?: Ref<CurrencyParameter> | CurrencyParameter,
) {
  const rawCurrency = computed(() => extractRef(currency));
  const rawTargetCurrency = computed(() => extractRef(targetCurrency));
  const bookingStore = useBookingStore();
  const currencies = useCurrenciesMap();

  const userCurrency = computed(() => {
    const targetCurrencyValue = rawTargetCurrency.value;

    if (targetCurrencyValue) {
      return currencies.value[targetCurrencyValue];
    }

    return currencies.value[bookingStore.currentCurrency];
  });

  const amountCurrency = computed(() =>
    rawCurrency.value
      ? currencies.value[rawCurrency.value as string]
      : undefined,
  );

  const sameCurrency = computed(
    () => userCurrency.value?.code === amountCurrency.value?.code,
  );

  return computed(() => {
    const rawAmount = extractRef(amount);

    if (sameCurrency.value)
      return {
        amount: rawAmount,
        currency: amountCurrency.value,
        sameCurrency: sameCurrency.value,
      };

    return {
      amount: convertTo(rawAmount, amountCurrency.value, userCurrency.value),
      currency: userCurrency.value,
      sameCurrency: sameCurrency.value,
    };
  });
}
